import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "../../lib/utils"

const badgeVariants = cva(
  "h-5 gap-1 rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium transition-all has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&>svg]:size-3! inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive overflow-hidden group/badge",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary:
          "border-zinc-500/30 bg-zinc-500/15 text-zinc-700 dark:border-zinc-400/30 dark:bg-zinc-400/20 dark:text-zinc-200 [a]:hover:bg-zinc-500/25 dark:[a]:hover:bg-zinc-400/30",
        success:
          "border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/20 dark:text-emerald-200 [a]:hover:bg-emerald-500/25 dark:[a]:hover:bg-emerald-400/30",
        warning:
          "border-amber-500/30 bg-amber-500/15 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/20 dark:text-amber-200 [a]:hover:bg-amber-500/25 dark:[a]:hover:bg-amber-400/30",
        destructive:
          "bg-destructive/10 [a]:hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 text-destructive dark:bg-destructive/20",
        // Notable but neither good nor bad — a transfer, a queue, an in-progress state. Sky rather than
        // emerald/red so it cannot be misread as an outcome. Matches ChurchNavBadgeVariant.Info.
        info:
          "border-sky-500/30 bg-sky-500/15 text-sky-700 dark:border-sky-400/30 dark:bg-sky-400/20 dark:text-sky-200 [a]:hover:bg-sky-500/25 dark:[a]:hover:bg-sky-400/30",
        outline:
          "border-border bg-transparent text-foreground [a]:hover:bg-muted [a]:hover:text-foreground",
        ghost:
          "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
